/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'

import {
    Box,
    Flex,
    Heading,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {useContentfulPageBySlug} from '../../clients/contentful/hooks'
import CategoryGrid from '../../components/category-grid'
import HeroSection from '../../components/hero-section'
import HeroImageSlider from '../../components/hero-section/hero-carousel'
import ServiceCard from '../../components/home-service-section/service-card'
import ProductScroller from '../../components/product-scroller'
import Seo from '../../components/seo'
import VideoSection from '../../components/video-section'
import {colors, SectionTypes} from '../../constants'
import {contentfulMediaFormatter} from '../../utils/contentful'
import IngredientGrid from './ingredient-grid'
import StructuredData from './schema/structured-data'
import NewsLetter from '../../components/newsletter'
import Scroller from '../../components/scroller'
import HomeScrollerCard from './scroller'
import Carousel from '../../components/carousel'
import HomeArticlesCarouselCard from './carousel'
import BookServiceCard from '../../components/book-service-card'
import CardsGridSection from './card-grid'
import RichTextComponent from '../../components/rich-text'

const ProductSection = ({fields}) => {
    const carouselProducts = (fields?.items ?? []).map((item) => {
        const asset = contentfulMediaFormatter(item?.fields?.image)
        return {
            ...item.fields,
            image: asset,
            brand: item?.fields?.brand?.fields ?? null
        }
    })

    return (
        <ProductScroller
            productCarouselContent={{
                carouselCollection: {
                    items: [
                        {
                            heading: fields?.heading,
                            cta: fields?.cta?.fields,
                            itemsCollection: {
                                items: carouselProducts
                            }
                        }
                    ]
                }
            }}
            isLoading={false}
            px={{base: '16px', lg: '32px', xl: '43px'}}
            showScrollerNav
            maxWidth={{
                base: 'calc(100vw - 40px)',
                lg: 'calc(100vw - 32px)'
            }}
            itemWidth={{
                base: 'calc(50% - 6.5px)',
                md: 'calc(33.33% - 8.66px)',
                lg: 'calc(25% - 9.75px)',
                xl: 'calc(25% - 9.75px)'
            }}
            cardsGap="13px"
            scrollContainerId="product-scroller-container"
        />
    )
}

ProductSection.propTypes = {
    fields: PropTypes.shape({
        items: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                name: PropTypes.string,
                type: PropTypes.string,
                required: PropTypes.bool,
                placeholder: PropTypes.string,
                errorMessage: PropTypes.string
            })
        ),
        heading: PropTypes.string,
        image: PropTypes.object,
        cta: PropTypes.object
    })
}

const ServiceCardSection = ({fields}) => (
    <>
        {fields?.items?.map(({sys, fields}, ind) => {
            const asset = contentfulMediaFormatter(fields?.image)

            return (
                <ServiceCard
                    key={`${sys?.id}-${ind}`}
                    serviceCard={{
                        ...fields,
                        cta: fields?.cta?.fields,
                        image: asset
                    }}
                    variant="service"
                />
            )
        })}
    </>
)

ServiceCardSection.propTypes = {
    fields: PropTypes.shape({
        items: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                name: PropTypes.string,
                type: PropTypes.string,
                required: PropTypes.bool,
                placeholder: PropTypes.string,
                errorMessage: PropTypes.string
            })
        ),
        sectionType: PropTypes.string,
        image: PropTypes.object,
        cta: PropTypes.object
    })
}

const IngredientGlossarySection = ({fields}) => {
    const items = (fields?.items ?? []).map(({fields}) => {
        const asset = contentfulMediaFormatter(fields?.media)
        return {
            ...fields,
            media: asset,
            cta: fields?.cta?.fields
        }
    })

    return (
        <IngredientGrid
            data={{
                itemsCollection: {items}
            }}
        />
    )
}

IngredientGlossarySection.propTypes = {
    fields: PropTypes.shape({
        items: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                name: PropTypes.string,
                type: PropTypes.string,
                required: PropTypes.bool,
                placeholder: PropTypes.string,
                errorMessage: PropTypes.string
            })
        ),
        sectionType: PropTypes.string,
        media: PropTypes.object,
        cta: PropTypes.object
    })
}

const DefaultContentGridSection = ({fields}) => (
    <>
        {fields?.items?.map(({sys, fields}, ind) => {
            const asset = contentfulMediaFormatter(fields?.image)
            const item = {
                ...fields,
                cta: fields?.cta?.fields,
                image: asset
            }

            return (
                <ServiceCard
                    key={`${sys?.id}-${ind}`}
                    serviceCard={item}
                    variant="service"
                />
            )
        })}
    </>
)

DefaultContentGridSection.propTypes = {
    fields: PropTypes.shape({
        items: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                name: PropTypes.string,
                type: PropTypes.string,
                required: PropTypes.bool,
                placeholder: PropTypes.string,
                errorMessage: PropTypes.string
            })
        ),
        sectionType: PropTypes.string,
        image: PropTypes.object,
        cta: PropTypes.object
    })
}

const ProductCarouselScroller = ({fields}) => {
    const items = (fields?.items ?? []).map(({sys, fields: product}, ind) => (
        <HomeScrollerCard key={`${sys?.id}-${ind}`} product={product} />
    ))

    return (
        <Flex
            width="100%"
            justifyContent="center"
            py={{base: '24px', xl: '40px'}}
        >
            <Scroller data={{...fields, items}} />
        </Flex>
    )
}

ProductCarouselScroller.propTypes = {
    fields: PropTypes.shape({
        items: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                name: PropTypes.string,
                type: PropTypes.string,
                required: PropTypes.bool,
                placeholder: PropTypes.string,
                errorMessage: PropTypes.string
            })
        ),
        sectionType: PropTypes.string,
        image: PropTypes.object,
        cta: PropTypes.object
    })
}

const Section = ({sys, fields}) => {
    const sectionType = sys?.contentType?.sys?.id

    const itemscomponentMapping = {
        homeHeroSection: <HeroImageSlider carousel={fields} />,
        product: <ProductSection fields={fields} />,
        serviceCard: <ServiceCardSection fields={fields} />,
        'Cards Grid with left title': (
            <CardsGridSection fields={fields} leftTitle />
        ),
        'Cards Grid': <CardsGridSection fields={fields} />,
        'Category Grid': <CategoryGrid data={fields} />,
        'Product Carousel': <ProductCarouselScroller fields={fields} />,
        'Ingredient Glossary': <IngredientGlossarySection fields={fields} />,
        defaultContentGrid: <DefaultContentGridSection fields={fields} />
    }

    switch (sectionType) {
        case SectionTypes.carousel:
            // eslint-disable-next-line no-case-declarations
            const internalSectionTypes = fields?.items?.map(
                (field) => field?.sys?.contentType?.sys?.id
            )
            // eslint-disable-next-line no-case-declarations
            const internalSectionType = Array.isArray(internalSectionTypes)
                ? internalSectionTypes.at(-1)
                : internalSectionTypes

            if (internalSectionType)
                return itemscomponentMapping[internalSectionType]

            return <></>
        case SectionTypes.homeHeroSection:
            return (
                <HeroSection data={fields} marginX={{base: '17px', md: '0'}} />
            )
        case SectionTypes.textAsset:
            return (
                <VideoSection
                    videoSectionData={{
                        ...fields,
                        media: contentfulMediaFormatter(fields?.media)
                    }}
                    borderRadius="20px"
                    py={{base: '20px', md: 'none'}}
                />
            )
        case SectionTypes.contentGrid:
            if (fields?.sectionType)
                return itemscomponentMapping[fields?.sectionType]

            return itemscomponentMapping.defaultContentGrid
        case SectionTypes.newsletter:
            return <NewsLetter data={fields} />
        case SectionTypes.bookServiceCard:
            return (
                <Flex
                    width="100%"
                    justifyContent="center"
                    px={{base: '24px', xl: '40px'}}
                >
                    <BookServiceCard data={fields} />
                </Flex>
            )
        case SectionTypes.articleCarousel:
            return (
                <Flex
                    my="40px"
                    maxWidth="100vw"
                    paddingY="40px"
                    minHeight="284px"
                    flexFlow="column"
                    marginBottom="40px"
                    position="relative"
                    alignItems="center"
                    backgroundColor={
                        fields?.theme?.fields?.backgroundColor ??
                        colors.footerBgGradient
                    }
                >
                    {fields?.sectionTitle && (
                        <Text
                            fontSize={{base: '24px', lg: '48px'}}
                            fontWeight="500"
                            lineHeight="32px"
                            fontFamily="Raleway"
                            color={colors.black}
                            marginBottom="32px"
                        >
                            {fields?.sectionTitle}
                        </Text>
                    )}
                    <Carousel
                        items={(fields?.items ?? [])?.map(
                            ({sys, fields: article}, ind) => (
                                <HomeArticlesCarouselCard
                                    // eslint-disable-next-line react/prop-types
                                    key={`${sys?.id}-${ind}`}
                                    data={article}
                                />
                            )
                        )}
                    />
                </Flex>
            )
        case SectionTypes.richTextContent:
            return (
                <Flex
                    width="100%"
                    justifyContent="center"
                    px={{base: '24px', xl: '40px'}}
                >
                    <RichTextComponent
                        data={fields}
                        textAlign="center"
                        alignItems="center"
                        width={{base: '100%', lg: '60%'}}
                        paddingLeft={{base: 0, lg: 0}}
                        paddingRight={{base: 0, lg: 0}}
                    />
                </Flex>
            )
        default:
            return <></>
    }
}

Section.propTypes = {
    sys: PropTypes.shape({
        contentType: PropTypes.shape({
            sys: PropTypes.shape({
                id: PropTypes.string
            })
        })
    }),
    fields: PropTypes.shape({
        items: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                name: PropTypes.string,
                type: PropTypes.string,
                required: PropTypes.bool,
                placeholder: PropTypes.string,
                errorMessage: PropTypes.string
            })
        ),
        sectionTitle: PropTypes.string,
        sectionType: PropTypes.string,
        media: PropTypes.object,
        theme: PropTypes.object
    })
}

/**
 * This is the home page for Retail React App.
 * The page is created for demonstration purposes.
 * The page renders SEO metadata and a few promotion
 * categories and products, data is from local file.
 */

const Home = () => {
    const {data} = useContentfulPageBySlug('home')
    const {metaTitle, metaDescription} = useMemo(() => {
        return {
            metaTitle: data?.metaPageTitle,
            metaDescription: data?.metaDescription
        }
    }, [data])

    return (
        <Box
            data-testid="home-page"
            layerStyle="homePage"
            gap={{base: '30px', lg: '80px'}}
            width="100%"
        >
            <Seo
                title={metaTitle}
                description={metaDescription}
                keywords="Rennaï, MountRoyal, Canada, Montreal"
            />
            <StructuredData />
            <Heading as="h1" display={{base: 'none'}}>
                {`${data?.metaPageTitle || 'Home'} | Rennaï`}
            </Heading>
            <Flex
                width="100%"
                flexFlow="column"
                gap={{base: '30px', lg: '24px'}}
                maxWidth="100vw"
            >
                {data?.sections?.map((section, ind) => (
                    <Section key={`${section?.sys?.id}-${ind}`} {...section} />
                ))}
            </Flex>
        </Box>
    )
}

Home.getTemplateName = () => 'home'

export default Home
